import React from 'react';
import { motion } from 'framer-motion';
interface KofschipMnemonicProps {
  verb: string;
  stem: string;
  lastLetter: string;
  useTE: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function KofschipMnemonic({
  verb,
  stem,
  lastLetter,
  useTE,
  label,
  mode = 'support'
}: KofschipMnemonicProps) {
  const kofschipLetters = ['K', 'O', 'F', 'S', 'C', 'H', 'I', 'P'];
  const isInKofschip = kofschipLetters.includes(lastLetter.toUpperCase());
  // ── STIMULUS MODE: compact KOFSCHIP grid + result only ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-5471">
        {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-5472">
            {label}
          </p>}
        {/* Compact KOFSCHIP grid */}
        <div className="flex items-center gap-1.5" data-id="element-5473">
          <span className="text-base font-black text-slate-400 mr-1" data-id="element-5474">'t</span>
          {kofschipLetters.map(letter => {
          const isMatch = letter === lastLetter.toUpperCase();
          return <div key={letter} className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg shadow-sm
                  ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-white text-slate-600 border border-slate-200'}`} data-id="element-5475">
                {letter}
              </div>;
        })}
        </div>
        {/* Compact result badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 font-bold text-sm
          ${isInKofschip ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-orange-50 border-orange-300 text-orange-800'}`} data-id="element-5476">
          <span className="font-black text-base" data-id="element-5477">{stem}</span>
          <span className="opacity-60" data-id="element-5478">+</span>
          <span className="font-black text-base" data-id="element-5479">{useTE ? '-te' : '-de'}</span>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-5480">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border-2 border-blue-200 shadow-lg" data-id="element-5481">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-5482">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-5483">
            <span className="text-2xl" data-id="element-5484">⛵</span>
            <div className="text-left" data-id="element-5485">
              <div className="text-sm font-bold text-blue-900" data-id="element-5486">
                {label || "'t KOFSCHIP Regel"}
              </div>
              <div className="text-xs text-blue-600 font-semibold" data-id="element-5487">
                Verleden Tijd Spelling
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Verb → Stem */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-5488">
          <div className="flex items-start gap-3" data-id="element-5489">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-5490">
              <span className="text-blue-600 font-bold text-sm" data-id="element-5491">1</span>
            </div>
            <div className="flex-1" data-id="element-5492">
              <h4 className="text-sm font-bold text-blue-900 mb-3" data-id="element-5493">
                Stap 1: Vind de stam
              </h4>
              <div className="flex items-center gap-3 flex-wrap" data-id="element-5494">
                <div className="text-center" data-id="element-5495">
                  <div className="text-xs text-slate-500 font-semibold mb-1" data-id="element-5496">
                    Werkwoord
                  </div>
                  <div className="text-xl font-black text-blue-700 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200" data-id="element-5497">
                    {verb}
                  </div>
                </div>
                <div className="text-2xl text-slate-300" data-id="element-5498">→</div>
                <div className="text-center" data-id="element-5499">
                  <div className="text-xs text-slate-500 font-semibold mb-1" data-id="element-5500">
                    Stam (-en weg)
                  </div>
                  <div className="text-xl font-black text-purple-700 bg-purple-50 px-4 py-2 rounded-lg border border-purple-200" data-id="element-5501">
                    {stem}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Last Letter Check */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-cyan-100 shadow-sm" data-id="element-5502">
          <div className="flex items-start gap-3" data-id="element-5503">
            <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0" data-id="element-5504">
              <span className="text-cyan-600 font-bold text-sm" data-id="element-5505">2</span>
            </div>
            <div className="flex-1" data-id="element-5506">
              <h4 className="text-sm font-bold text-cyan-900 mb-3" data-id="element-5507">
                Stap 2: Kijk naar de laatste letter van de stam
              </h4>
              <div className="text-center" data-id="element-5508">
                <div className="inline-flex items-center gap-2 bg-cyan-50 px-6 py-3 rounded-xl border-2 border-cyan-200" data-id="element-5509">
                  <span className="text-sm text-slate-600 font-semibold" data-id="element-5510">
                    Laatste letter:
                  </span>
                  <span className="text-3xl font-black text-cyan-700" data-id="element-5511">
                    {lastLetter.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: KOFSCHIP Check */}
        <div className="mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border-2 border-indigo-200 shadow-md" data-id="element-5512">
          <div className="flex items-start gap-3 mb-4" data-id="element-5513">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-5514">
              <span className="text-indigo-600 font-bold text-sm" data-id="element-5515">3</span>
            </div>
            <div data-id="element-5516">
              <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-5517">
                Stap 3: Staat de letter in 't KOFSCHIP?
              </h4>
              <p className="text-xs text-slate-600 italic" data-id="element-5518">
                't KOFSCHIP is een ezelsbruggetje om te onthouden welke letters
                -te krijgen
              </p>
            </div>
          </div>
          <div className="flex justify-center gap-2 mb-4" data-id="element-5519">
            <div className="text-2xl font-black text-slate-400 mr-2" data-id="element-5520">'t</div>
            {kofschipLetters.map((letter, index) => {
            const isMatch = letter === lastLetter.toUpperCase();
            return <motion.div key={letter} initial={{
              scale: 0,
              rotate: -10
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              delay: index * 0.05,
              type: 'spring',
              stiffness: 300
            }} className={`w-12 h-12 rounded-lg flex items-center justify-center font-black text-2xl transition-all duration-300 shadow-md
                    ${isMatch ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white ring-4 ring-green-200 scale-110' : 'bg-white text-slate-700 border-2 border-slate-200'}`} data-id="element-5521">
                  {letter}
                </motion.div>;
          })}
          </div>
          <div className={`text-center p-4 rounded-xl border-2 font-bold ${isInKofschip ? 'bg-green-50 border-green-200 text-green-800' : 'bg-orange-50 border-orange-200 text-orange-800'}`} data-id="element-5522">
            {isInKofschip ? <div data-id="element-5523">
                <div className="text-lg mb-1" data-id="element-5524">
                  ✅ Ja! De letter{' '}
                  <span className="text-2xl" data-id="element-5525">{lastLetter.toUpperCase()}</span>{' '}
                  zit in 't KOFSCHIP
                </div>
                <div className="text-sm" data-id="element-5526">
                  Dus: verleden tijd krijgt <strong data-id="element-5527">-te</strong> (of{' '}
                  <strong data-id="element-5528">-tte</strong> bij meervoud)
                </div>
              </div> : <div data-id="element-5529">
                <div className="text-lg mb-1" data-id="element-5530">
                  ❌ Nee! De letter{' '}
                  <span className="text-2xl" data-id="element-5531">{lastLetter.toUpperCase()}</span>{' '}
                  zit NIET in 't KOFSCHIP
                </div>
                <div className="text-sm" data-id="element-5532">
                  Dus: verleden tijd krijgt <strong data-id="element-5533">-de</strong> (of{' '}
                  <strong data-id="element-5534">-dde</strong> bij meervoud)
                </div>
              </div>}
          </div>
        </div>

        <div className="space-y-4" data-id="element-5535">
          <div className="bg-white/80 rounded-xl p-5 border border-emerald-100 shadow-sm" data-id="element-5536">
            <div className="flex items-start gap-3" data-id="element-5537">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-5538">
                <span className="text-emerald-600 text-lg" data-id="element-5539">📝</span>
              </div>
              <div data-id="element-5540">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-5541">
                  Voorbeeld met dit werkwoord
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-5542">
                  <p data-id="element-5543">
                    <strong data-id="element-5544">Stam:</strong> {stem}
                  </p>
                  <p data-id="element-5545">
                    <strong data-id="element-5546">Laatste letter:</strong> {lastLetter.toUpperCase()}
                    {isInKofschip ? ' (zit in KOFSCHIP ✓)' : ' (zit NIET in KOFSCHIP ✗)'}
                  </p>
                  <p data-id="element-5547">
                    <strong data-id="element-5548">Verleden tijd:</strong>{' '}
                    <span className="font-semibold text-emerald-700" data-id="element-5549">
                      {stem}
                      {useTE ? 'te' : 'de'}
                    </span>{' '}
                    (enkelvoud) /{' '}
                    <span className="font-semibold text-emerald-700" data-id="element-5550">
                      {stem}
                      {useTE ? 'tten' : 'dden'}
                    </span>{' '}
                    (meervoud)
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-5551">
            <div className="flex items-start gap-3" data-id="element-5552">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-5553">
                <span className="text-amber-600 text-lg" data-id="element-5554">💡</span>
              </div>
              <div data-id="element-5555">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-5556">
                  Ezelsbruggetje
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5557">
                  Onthoud: <strong data-id="element-5558">'t KOFSCHIP</strong> vaart door de haven. Als
                  de laatste letter van de stam in dit woord zit, gebruik je{' '}
                  <strong data-id="element-5559">-te</strong>. Anders gebruik je <strong data-id="element-5560">-de</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}